import { Box } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useContactCenterTheme } from "../../virtual-receptionist/theming/ContactCenterThemeContext";
import { Message } from "./Message/Message";
import { PendingMessageBubble } from "./Message/PendingMessageBubble";

const MAX_FOCUS_PAGE_FETCHES = 20;

export const MessagesList = ({
  messagesQuery,
  messagesContainerRef,
  handleScroll,
  sender,
  onOpenImagePreview,
  maskNonOwnSenderName,
  renderMessage,
  pendingMessages = [],
  onRetryMessage,
  resolveIsRead,
  focusMessageUid,
  focusMessageText,
  focusMessageRequestKey,
  onFocusMessageConsumed,
}) => {
  const { theme } = useContactCenterTheme();
  const { data } = messagesQuery;
  const { fetchNextPage, hasNextPage, isLoading } = messagesQuery;
  const messageRefs = useRef({});
  const [highlightedKey, setHighlightedKey] = useState(null);

  const messages = useMemo(() => {
    return data?.pages ? [...data.pages].reverse().flatMap((page) => [...(page?.items || [])].reverse()) : [];
  }, [data]);

  const normalizeText = useCallback((value) => String(value ?? "").trim().replace(/\s+/g, " ").toLowerCase(), []);

  const getMessageKey = useCallback((message) => {
    const key = message?.uid ?? message?.id;
    return key == null ? null : String(key);
  }, []);

  const getMessageText = useCallback((message) => {
    const content = message?.content;
    return content?.text ?? content?.body ?? content?.message ?? content?.Body ?? message?.text ?? message?.message ?? "";
  }, []);

  const findFocusKey = useCallback(
    (items) => {
      if (focusMessageUid) {
        const targetKey = String(focusMessageUid);
        const exactMatch = items.find((item) => getMessageKey(item) === targetKey);
        if (exactMatch) return targetKey;
      }

      const normalizedFocusText = normalizeText(focusMessageText);
      if (!normalizedFocusText) return null;

      for (let index = items.length - 1; index >= 0; index -= 1) {
        const key = getMessageKey(items[index]);
        if (!key) continue;

        const normalizedMessageText = normalizeText(getMessageText(items[index]));
        if (normalizedMessageText === normalizedFocusText || normalizedMessageText.includes(normalizedFocusText)) return key;
      }

      return null;
    },
    [focusMessageUid, focusMessageText, getMessageKey, getMessageText, normalizeText],
  );

  const scrollToMessage = useCallback((key) => {
    const element = messageRefs.current[key];
    if (!element) return false;

    element.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightedKey(key);

    setTimeout(() => {
      setHighlightedKey((current) => (current === key ? null : current));
    }, 2200);

    return true;
  }, []);

  const consumeFocusMessage = useCallback(() => {
    if (!onFocusMessageConsumed) return;
    setTimeout(() => onFocusMessageConsumed(), 900);
  }, [onFocusMessageConsumed]);

  useEffect(() => {
    if (!focusMessageUid && !focusMessageText) return;
    if (isLoading || !messages.length) return;

    let cancelled = false;

    const focusMessage = async () => {
      await new Promise((resolve) => setTimeout(resolve, 120));

      const visibleKey = findFocusKey(messages);
      if (cancelled) return;
      if (visibleKey) {
        setTimeout(() => scrollToMessage(visibleKey), 120);
        setTimeout(() => scrollToMessage(visibleKey), 450);
        consumeFocusMessage();
        return;
      }

      let foundKey = null;
      let canFetchMore = Boolean(hasNextPage);
      let fetches = 0;

      while (!cancelled && !foundKey && canFetchMore && fetches < MAX_FOCUS_PAGE_FETCHES) {
        fetches += 1;

        const result = await fetchNextPage();
        const loadedMessages = result.data?.pages.flatMap((page) => page.items || []) ?? [];
        const lastPage = result.data?.pages?.[result.data.pages.length - 1];
        const page = Number(lastPage?.page);
        const totalPages = Number(lastPage?.totalPages);

        foundKey = findFocusKey(loadedMessages);
        canFetchMore = lastPage?.meta?.hasNextPage ?? (Number.isFinite(page) && Number.isFinite(totalPages) && page < totalPages);
      }

      if (!cancelled && foundKey) {
        setTimeout(() => scrollToMessage(foundKey), 120);
        setTimeout(() => scrollToMessage(foundKey), 450);
      }

      if (!cancelled) consumeFocusMessage();
    };

    focusMessage();

    return () => {
      cancelled = true;
    };
  }, [
    focusMessageUid,
    focusMessageText,
    focusMessageRequestKey,
    messages,
    isLoading,
    hasNextPage,
    fetchNextPage,
    findFocusKey,
    scrollToMessage,
    consumeFocusMessage,
  ]);

  return (
    <Box
      onScroll={handleScroll}
      ref={messagesContainerRef}
      sx={{
        flex: 1,
        overflowY: "auto",
        p: 3,
        pb: 1,
        backgroundColor: theme.chat.bg,
        background: theme.chat.pattern ? `${theme.chat.pattern}, ${theme.chat.bg}` : undefined,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {messages.map((msg) => (
        <Box
          key={msg.uid || msg.id}
          ref={(element) => {
            const key = getMessageKey(msg);
            if (key) messageRefs.current[key] = element;
          }}
          sx={{
            borderRadius: 2,
            bgcolor: highlightedKey === getMessageKey(msg) ? theme.chat.highlight : "transparent",
            transition: "background-color 0.25s ease",
          }}
        >
          {renderMessage ? (
            renderMessage(msg)
          ) : (
            <Message
              message={msg}
              sender={sender}
              onOpenImagePreview={onOpenImagePreview}
              maskNonOwnSenderName={maskNonOwnSenderName}
              resolveIsRead={resolveIsRead}
            />
          )}
        </Box>
      ))}
      {pendingMessages.map((entry) => (
        <PendingMessageBubble key={entry.clientTempId} entry={entry} onRetry={onRetryMessage} />
      ))}
    </Box>
  );
};
